/* ============================================================================
   AKINOD — Page catégorie
   ----------------------------------------------------------------------------
   Le filtrage lui-même est l'affaire de Shopify : chaque option est un lien
   vers `url_to_add` / `url_to_remove`, chaque compteur vient du serveur. Rien
   de tout cela n'est repris ici. Ce fichier n'ajoute que du confort :

     • le tiroir de filtres — ouverture, voile, Échap, piège à focus ;
     • le popover de tri ;
     • « Afficher plus » qui ajoute la page suivante sans recharger.

   Les puces des mini-carrousels de cartes ne sont PAS ici : elles sont
   rallumées par `assets/akinod-behaviors.js` (`allumerPuce`, sur `[data-ak-dots]`),
   chargé sur toutes les pages. Une seconde implémentation vivait ici et faisait
   le même travail une deuxième fois ; c'est la raison pour laquelle les rangées
   de catégories et « Consulté récemment » chargeaient ce fichier sans utiliser
   rien d'autre.

   Sans lui, la page reste entièrement utilisable : le tiroir n'est qu'un
   panneau de liens, et « Afficher plus » redevient un lien vers la page 2.

   Seule la grille de catégorie pose désormais cette balise <script>, et
   l'éditeur de thème réinjecte les sections sans recharger la page. Tout est
   donc idempotent, par marquage `data-ak-*-bound`.
   ========================================================================== */

(() => {
  'use strict';

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  /** Verrouille le défilement de la page derrière un panneau qui la recouvre.

     `document.body.style.overflow` ne verrouille rien au-dessus de 990 px :
     Horizon y met `html` et `body` en `overflow: hidden` et confie le
     défilement à `.page-wrapper` (voir `assets/base.css`). Le thème dispose
     pour cela d'un attribut, `html[scroll-lock]`, qui coupe les deux — c'est
     celui qu'utilisent les fenêtres modales (`assets/akinod-popup.js`,
     `assets/utilities.js`). On pose les deux : le style sur `body` reste juste
     en dessous de 990 px, et il ne gêne pas au-dessus. */
  function verrouiller(actif) {
    document.body.style.overflow = actif ? 'hidden' : '';
    document.documentElement.toggleAttribute('scroll-lock', actif);
  }

  /* --- Tiroir de filtres --------------------------------------------------- */

  function initFilters(root = document) {
    root.querySelectorAll('[data-ak-collection]:not([data-ak-filters-bound])').forEach((scope) => {
      const drawer = scope.querySelector('[data-ak-filters]');
      const openBtn = scope.querySelector('[data-ak-filters-open]');
      if (!drawer || !openBtn) return;

      scope.dataset.akFiltersBound = '1';
      const veil = scope.querySelector('[data-ak-filters-veil]');
      let lastFocused = null;

      const open = () => {
        lastFocused = document.activeElement;
        drawer.hidden = false;
        if (veil) veil.hidden = false;
        // Un frame de décalage, sinon la transition ne joue pas depuis `hidden`.
        requestAnimationFrame(() => {
          drawer.classList.add('is-open');
          veil?.classList.add('is-open');
        });
        openBtn.setAttribute('aria-expanded', 'true');
        openBtn.classList.add('is-on');
        verrouiller(true);
        drawer.querySelector('[data-ak-filters-close]')?.focus();
      };

      const close = () => {
        if (drawer.hidden) return;
        drawer.classList.remove('is-open');
        veil?.classList.remove('is-open');
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.classList.remove('is-on');
        verrouiller(false);

        /* Deux gardes sur cette fin de transition, une par défaut constaté :

           · la cible doit être le tiroir lui-même. Les transitions de ses
             enfants remontent jusqu'ici, et la première arrivée coupait
             l'animation du tiroir en le masquant d'emblée ;

           · le tiroir doit être encore fermé. Rouvert dans les 380 ms, c'était
             la transition d'OUVERTURE qui arrivait, et l'on masquait un tiroir
             pourtant ouvert — défilement du corps verrouillé, plus rien à
             l'écran pour le rendre. */
        const fin = (e) => {
          if (e.target !== drawer) return;
          drawer.removeEventListener('transitionend', fin);
          if (drawer.classList.contains('is-open')) return;
          drawer.hidden = true;
          if (veil) veil.hidden = true;
        };
        drawer.addEventListener('transitionend', fin);
        lastFocused?.focus();
      };

      openBtn.addEventListener('click', () => (drawer.hidden ? open() : close()));
      veil?.addEventListener('click', close);
      drawer.querySelectorAll('[data-ak-filters-close]').forEach((el) => el.addEventListener('click', close));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') close();
      });

      // Le tiroir recouvre la page : le focus n'a rien à faire derrière.
      drawer.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const items = [...drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });
    });
  }

  /* --- Popover de tri ------------------------------------------------------ */

  function initSort(root = document) {
    root.querySelectorAll('[data-ak-sort]:not([data-ak-sort-bound])').forEach((box) => {
      const btn = box.querySelector('[data-ak-sort-toggle]');
      const pop = box.querySelector('[data-ak-sort-pop]');
      if (!btn || !pop) return;

      box.dataset.akSortBound = '1';

      const setOpen = (open) => {
        pop.hidden = !open;
        btn.setAttribute('aria-expanded', String(open));
        btn.classList.toggle('is-on', open);
      };

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        setOpen(pop.hidden);
      });

      document.addEventListener('click', (e) => {
        if (!box.contains(e.target)) setOpen(false);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || pop.hidden) return;
        setOpen(false);
        btn.focus();
      });
    });
  }

  /* --- « Afficher plus » ---------------------------------------------------- */

  function initLoadMore(root = document) {
    root.querySelectorAll('[data-ak-load-more]:not([data-ak-more-bound])').forEach((btn) => {
      const scope = btn.closest('[data-ak-collection]') || document;
      const grid = scope.querySelector('[data-ak-grid]');
      if (!grid) return;

      btn.dataset.akMoreBound = '1';

      btn.addEventListener('click', async (e) => {
        const url = btn.href;
        if (!url || btn.classList.contains('is-busy')) return;
        e.preventDefault();

        btn.classList.add('is-busy');
        btn.setAttribute('aria-busy', 'true');

        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(res.status);
          const next = new DOMParser().parseFromString(await res.text(), 'text/html');

          const nextGrid = next.querySelector('[data-ak-grid]');
          if (nextGrid) {
            grid.append(...nextGrid.children);
            // Les nouvelles cartes ont besoin de leurs comportements : défilement
            // des mini-carrousels, flèches et puces.
            window.AkinodBehaviors?.boot(grid);
          }

          // Plus de page suivante : le bouton n'a plus lieu d'être.
          const nextBtn = next.querySelector('[data-ak-load-more]');
          if (nextBtn) {
            btn.href = nextBtn.href;
          } else {
            btn.closest('.ak-cgrid__more')?.remove();
          }
        } catch {
          // Repli : la navigation classique vers la page suivante.
          window.location.href = url;
        } finally {
          btn.classList.remove('is-busy');
          btn.removeAttribute('aria-busy');
        }
      });
    });
  }

  /* --- Amorçage ------------------------------------------------------------- */

  function boot(root) {
    initFilters(root);
    initSort(root);
    initLoadMore(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
